/**
 * Regras e formatações da Prestação de Contas (independentes de componente).
 * Lógica portada do formulário original: progresso por área, resumo executivo e Markdown.
 */
import { AREAS, AREA_ABERTURA } from './areas';

export const MESES = [
  'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
  'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro',
];

export const competenciaAtual = () => new Date().toISOString().slice(0, 7);

export const rotuloCompetencia = (competencia) => {
  const [ano, mes] = String(competencia || '').split('-');
  return `${MESES[Number(mes) - 1] || ''} de ${ano || ''}`.trim();
};

export const areaVazia = () => ({ responsavel: '', atualizado: '', campos: {} });

export const findArea = (id) => AREAS.find((a) => a.id === id);

/** Linhas de uma tabela, criando as padrão (fixas ou `min` vazias) quando ainda não existem. */
export function linhasDe(bloco, dadosArea) {
  let linhas = dadosArea.campos[bloco.k];
  if (!Array.isArray(linhas) || !linhas.length) {
    linhas = bloco.linhas
      ? bloco.linhas.map((l) => ({ ...l }))
      : Array.from({ length: bloco.min || 2 }, () => ({}));
    dadosArea.campos[bloco.k] = linhas;
  }
  return linhas;
}

const linhaPreenchida = (bloco, linha) =>
  Object.entries(linha).some(([k, v]) => v && !(bloco.cols.find((c) => c.k === k) || {}).fixo);

/** Percentual (0-100) de blocos preenchidos de uma área. */
export function progressoArea(area, dadosArea) {
  if (!dadosArea) return 0;
  let total = 0;
  let feitos = 0;
  for (const b of area.blocos) {
    if (b.t === 'kpis') {
      for (const c of b.campos) {
        total++;
        if (dadosArea.campos[c.k]) feitos++;
      }
    } else if (b.t === 'texto') {
      total++;
      if (dadosArea.campos[b.k]) feitos++;
    } else {
      total++;
      const linhas = dadosArea.campos[b.k] || [];
      if (linhas.some((l) => linhaPreenchida(b, l))) feitos++;
    }
  }
  return total ? Math.round((feitos / total) * 100) : 0;
}

export const LIMIAR_PRONTO = 70;

export function situacaoArea(progresso) {
  if (progresso >= LIMIAR_PRONTO) return { chave: 'ok', label: 'Pronto', badge: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300', barra: 'bg-emerald-500' };
  if (progresso > 0) return { chave: 'parcial', label: 'Em preenchimento', badge: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300', barra: 'bg-amber-400' };
  return { chave: 'vazio', label: 'Não iniciado', badge: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300', barra: 'bg-gray-300 dark:bg-gray-600' };
}

export const vazio = (v) => v === undefined || v === null || v === '';

/** Aceita "1.234,5", "1234.5" ou número; NaN quando não é numérico. */
export const paraNumero = (v) => {
  if (vazio(v)) return NaN;
  if (typeof v === 'number') return v;
  let s = String(v).trim().replace(/\s|R\$|%/g, '');
  if (s.includes(',') && s.includes('.')) s = s.replace(/\./g, '').replace(',', '.');
  else if (s.includes(',')) s = s.replace(',', '.');
  return Number(s);
};

export const numBR = (v) => {
  const n = paraNumero(v);
  return isNaN(n) ? String(v ?? '') : n.toLocaleString('pt-BR');
};

export const pct = (v) => {
  const n = paraNumero(v);
  return isNaN(n) ? '' : `${n.toLocaleString('pt-BR')}%`;
};

export const formatarDataHora = (iso) =>
  iso ? new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';

export const formatarData = (iso) =>
  iso ? new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '';

/** Linhas de tabela com algum valor não fixo — as demais não entram no relatório. */
export const linhasComConteudo = (bloco, dadosArea) =>
  ((dadosArea && dadosArea.campos[bloco.k]) || []).filter((l) =>
    bloco.cols.some((c) => !c.fixo && !vazio(l[c.k])));

/** KPIs do resumo executivo, puxados das respostas de cada área. */
export function resumoExecutivo(dados) {
  const valor = (areaId, campo) => (dados[areaId] && dados[areaId].campos ? dados[areaId].campos[campo] : undefined);
  const metas = valor('09-qualidade-indicadores', 'metas_atingidas');
  const tempoDiag = valor('07-navegacao', 'tempo_diag');
  return [
    ['Procedimentos realizados', numBR(valor('10-producao', 'realizados'))],
    ['Metas contratuais atingidas', vazio(metas) ? '' : `${metas} / 16`],
    ['Usuários satisfeitos', pct(valor('09-qualidade-indicadores', 'satisfacao'))],
    ['Tempo até o diagnóstico', vazio(tempoDiag) ? '' : `${tempoDiag} dias`],
    ['OS de manutenção concluídas', numBR(valor('01-infraestrutura', 'os_concluidas'))],
    ['Resolutividade da Eng. Clínica', pct(valor('03-engenharia-clinica', 'resolutividade'))],
    ['Disponibilidade dos sistemas', pct(valor('04-tecnologia-da-informacao', 'disponibilidade'))],
    ['Estudantes e residentes', numBR(valor('08-ensino-pesquisa', 'estudantes'))],
  ].filter(([, v]) => !vazio(v));
}

export const abertura = (dados) => dados[AREA_ABERTURA] || { campos: {} };

/** Exportação em Markdown (mesmo formato do formulário original). */
export function gerarMarkdown(competencia, dados) {
  const linhas = ['# Relatório de Gestão e Prestação de Contas — CCDTI', '', `Competência: ${rotuloCompetencia(competencia)}`, ''];
  for (const a of AREAS) {
    const d = dados[a.id];
    linhas.push(`## ${a.sec} · ${a.titulo}`, '');
    if (!d || progressoArea(a, d) === 0) {
      linhas.push('_Sem dados enviados._', '');
      continue;
    }
    if (d.responsavel) linhas.push(`Responsável: ${d.responsavel}`, '');
    for (const b of a.blocos) {
      if (b.t === 'kpis') {
        const itens = b.campos.filter((c) => d.campos[c.k]);
        if (itens.length) linhas.push(...itens.map((c) => `- **${c.l}:** ${d.campos[c.k]}`), '');
      } else if (b.t === 'texto') {
        if (d.campos[b.k]) linhas.push(`### ${b.h}`, '', d.campos[b.k], '');
      } else {
        const ls = linhasComConteudo(b, d);
        if (ls.length) {
          linhas.push(
            `### ${b.h}`, '',
            `| ${b.cols.map((c) => c.l).join(' | ')} |`,
            `|${b.cols.map(() => '---').join('|')}|`,
            ...ls.map((l) => `| ${b.cols.map((c) => l[c.k] ?? '').join(' | ')} |`),
            '',
          );
        }
      }
    }
  }
  return linhas.join('\n');
}

/** Dispara o download de um conteúdo (texto ou Blob) no navegador. */
export function baixarArquivo(nome, conteudo, tipo = 'text/plain;charset=utf-8') {
  const blob = conteudo instanceof Blob ? conteudo : new Blob([conteudo], { type: tipo });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nome;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}
